import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { fetchExecutions, fetchReruns } from "./services/exportJobsService";
import { ExecutionLedgerModal } from "./ExecutionLedgerModal";
import { RerunExportModal } from "./RerunExportModal";

const STATUS_STYLES = {
  pending:    { bg: "#F5F5F5", color: "#7b7b7b" },
  processing: { bg: "#EEF2FF", color: "#323084" },
  completed:  { bg: "#d8fffa", color: "#208a3e" },
  failed:     { bg: "#FFF5F5", color: "#e53935" },
};

// Manual re-run's terminal export_job_reruns.status values — the poller
// below stops once the triggered row reaches one of these.
const TERMINAL_RERUN_STATUSES = ["completed", "failed"];
const RERUN_POLL_INTERVAL_MS = 3000;
// Review finding #2: the poller used to discard fetchReruns' error entirely
// and never bounded its attempts, so a deleted/missing row or a persistently
// failing fetch left pollingRerunId set — and the 3s interval running — for
// the life of the panel with no visible signal that the re-run's status is
// unknown. 40 attempts * 3s = ~2 minutes, generous for a re-run to reach a
// terminal status before the panel gives up and tells the user to refresh.
export const MAX_RERUN_POLL_ATTEMPTS = 40;

// Pure decision helper for one poll tick — extracted so the stop/continue
// logic (terminal status, fetch error, attempt bound) is unit-testable
// without depending on real interval timing.
export function evaluateRerunPoll({ attempts, row, error }) {
  if (error) {
    return { stop: true, error: `Lost track of the re-run's status: ${error.message}. Refresh the page to check manually.` };
  }
  if (row && TERMINAL_RERUN_STATUSES.includes(row.status)) {
    return { stop: true, terminal: true };
  }
  if (attempts >= MAX_RERUN_POLL_ATTEMPTS) {
    return { stop: true, error: "Still waiting on the re-run's status after several minutes — refresh the page to check manually." };
  }
  return { stop: false };
}

function formatTimestamp(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

// window_from/window_to are timestamptz — rendered as plain dates (the
// half-open [window_from, window_to) day range the operator picked), not
// locale-formatted like an execution's own started_at, so the covered
// window reads unambiguously regardless of viewer timezone/locale.
function formatWindowDate(ts) {
  if (!ts) return "—";
  return String(ts).slice(0, 10);
}

// Per-job chronological execution history — timestamp, status, rows_exported,
// rows_failed. Modeled on TemplatesTab.jsx's list pattern. Row click opens
// ExecutionLedgerModal for the per-event drill-down. Support users
// additionally get a "Re-run dates" action (RerunExportModal) to
// re-transmit a past window's real conversions (074/DEV-2036 follow-up);
// while a triggered re-run is still pending/processing, this panel polls
// export_job_reruns for that row until it reaches a terminal status, then
// reloads execution history so the new manual_rerun row appears.
export function ExportExecutionHistoryPanel({ job }) {
  const { isSupportUser } = useAuth();
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [expandedResponseIds, setExpandedResponseIds] = useState(() => new Set());
  const [showRerunModal, setShowRerunModal] = useState(false);
  const [pollingRerunId, setPollingRerunId] = useState(null);

  // useCallback so `load` is referentially stable for a given job, and can
  // therefore sit in both effects' dependency arrays honestly rather than
  // being silenced with an eslint-disable. It closes over nothing but
  // `job?.id` and the state setters, which React guarantees are stable.
  const load = useCallback(() => {
    if (!job?.id) { setLoading(false); return () => {}; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchExecutions(job.id).then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) setError(err.message);
      setExecutions(data || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [job?.id]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    if (!pollingRerunId || !job?.id) return;
    let cancelled = false;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      const { data, error: err } = await fetchReruns(job.id);
      if (cancelled) return;
      const row = (data || []).find((r) => r.id === pollingRerunId);
      const result = evaluateRerunPoll({ attempts, row, error: err });
      if (result.stop) {
        setPollingRerunId(null);
        if (result.terminal) load();
        if (result.error) setError(result.error);
      }
    }, RERUN_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [pollingRerunId, job?.id, load]);

  const handleRerunTriggered = (rerun) => {
    if (rerun?.id && !TERMINAL_RERUN_STATUSES.includes(rerun.status)) {
      setPollingRerunId(rerun.id);
    } else {
      load();
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ fontSize: 28, fontWeight: 600, color: "#151515", fontFamily: "'Clash Display Variable','Clash Display',sans-serif" }}>
          Execution History{job?.export_templates?.platform_name ? ` — ${job.export_templates.platform_name}` : ""}
        </h3>
        {isSupportUser && (
          <button
            onClick={() => setShowRerunModal(true)}
            style={{ padding: "9px 16px", background: "#FFE9F6", color: "#F90091", border: "1px solid #F90091", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            Re-run dates
          </button>
        )}
      </div>

      {loading && <div style={{ color: "#7b7b7b", fontSize: 13 }}>Loading executions…</div>}
      {error && <div style={{ color: "#e53935", fontSize: 13 }}>{error}</div>}
      {!loading && !error && executions.length === 0 && <div style={{ color: "#7b7b7b", fontSize: 13 }}>No runs yet.</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {executions.map((ex) => {
          const s = STATUS_STYLES[ex.status] || STATUS_STYLES.pending;
          const responseExpanded = expandedResponseIds.has(ex.id);
          const toggleResponse = (e) => {
            e.stopPropagation();
            setExpandedResponseIds((prev) => {
              const next = new Set(prev);
              if (next.has(ex.id)) next.delete(ex.id);
              else next.add(ex.id);
              return next;
            });
          };
          return (
            <div
              key={ex.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedExecution(ex)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedExecution(ex); } }}
              style={{ background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 10, padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8, cursor: "pointer" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 12, color: "#151515", fontWeight: 500 }}>{formatTimestamp(ex.started_at || ex.created_at)}</div>
                </div>
                <span style={{ fontSize: 12, padding: "2px 7px", borderRadius: 20, background: s.bg, color: s.color, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{ex.status}</span>
                <div style={{ fontSize: 12, color: "#208a3e" }}>{ex.rows_exported} delivered</div>
                <div style={{ fontSize: 12, color: ex.rows_failed > 0 ? "#e53935" : "#7b7b7b" }}>{ex.rows_failed} failed</div>
                {ex.run_type === "manual_rerun" && (
                  <span style={{ fontSize: 12, padding: "2px 7px", borderRadius: 20, background: "#EDE7FF", color: "#6C63FF", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
                    Manual Re-run
                  </span>
                )}
              </div>

              {/* sent_at on this run's ledger rows reads as today — the
                  covered window (from the joined export_job_reruns request
                  row) is the only place a re-run's actual data period is
                  visible on this row. */}
              {ex.run_type === "manual_rerun" && ex.export_job_reruns && (
                <div style={{ fontSize: 12, color: "#6C63FF" }}>
                  Covers: {formatWindowDate(ex.export_job_reruns.window_from)} – {formatWindowDate(ex.export_job_reruns.window_to)}
                </div>
              )}

              {/* Whole-run failure detail (e.g. SFTP connection refused) — a whole-run
                  failure writes zero export_job_ledger rows, so this is the only place
                  the reason is ever visible. */}
              {ex.status === "failed" && ex.error_message && (
                <div style={{ fontSize: 13, color: "#e53935", background: "#FFF5F5", borderRadius: 6, padding: "6px 10px" }}>{ex.error_message}</div>
              )}

              {ex.response_body && (
                <div>
                  <button
                    type="button"
                    onClick={toggleResponse}
                    style={{ background: "none", border: "none", color: "#7b7b7b", fontSize: 11, cursor: "pointer", padding: 0 }}
                  >
                    {responseExpanded ? "Hide response" : "Show response"}
                  </button>
                  {responseExpanded && (
                    <pre style={{ marginTop: 6, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 11, color: "#626262", background: "#FAFAFA", border: "1px solid #E2E2E2", borderRadius: 6, padding: "8px 10px" }}>
                      {ex.response_body}
                    </pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedExecution && (
        <ExecutionLedgerModal execution={selectedExecution} onClose={() => setSelectedExecution(null)} />
      )}

      {showRerunModal && (
        <RerunExportModal job={job} onClose={() => setShowRerunModal(false)} onTriggered={handleRerunTriggered} />
      )}
    </div>
  );
}
